import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Cake, PartyPopper, CalendarDays, Send, Trophy, Plus, CalendarHeart, Settings } from "lucide-react";
import importantDatesService from "../services/importantDatesService";
import AuthContext from "../context/AuthContext";
import { getAvatarStyle } from "../utils/avatarColors";
import WidgetShell from "./widgets/WidgetShell";
import { useTranslation, useFormat } from "../i18n";

const getUserInitials = (name) => {
  if (!name) return "??";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
};

// `fmt` is the useFormat() bundle and `t` the feed-namespace translator of
// the calling component.
const formatDate = (dateString, fmt, t) => {
  const date = new Date(`${dateString}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((date - today) / 86400000);
  if (diffDays === 0) return t("dates.today");
  if (diffDays === 1) return t("dates.tomorrow");
  if (diffDays === -1) return t("dates.yesterday");
  return fmt.date(date, { month: "short", day: "numeric" });
};

// One row is a two-line label (20px + 16px) inside 6px of vertical padding,
// and the body pads 8px top and bottom. The height budget is built from these
// rather than picked by eye, so the fold always lands between rows instead of
// slicing a date in half.
const ROW_HEIGHT = 48;
const BODY_PADDING = 16;
const bodyHeightForRows = (rows) => BODY_PADDING + ROW_HEIGHT * rows;

// Labels are catalog keys, resolved with t(labelKey) inside the component.
const KIND_META = {
  birthday: { Icon: Cake, labelKey: "dates.birthday", color: "#ec4899" },
  anniversary: { Icon: PartyPopper, labelKey: "dates.anniversary", color: "#0ea5e9" },
  event: { Icon: CalendarDays, labelKey: "dates.event", color: "#8b5cf6" },
};

const ImportantDateRow = ({ item, isAdmin, currentUserId, onAction }) => {
  const { t } = useTranslation("feed");
  const fmt = useFormat();
  const meta = KIND_META[item.kind] || KIND_META.event;
  const { Icon } = meta;

  const isPerson = item.kind === "birthday" || item.kind === "anniversary";
  const isSelf = isPerson && item.user?.id === currentUserId;

  let hint = null;
  let HintIcon = null;
  let clickable = false;
  if (isPerson) {
    if (!isSelf) {
      hint = t("dates.send");
      HintIcon = Send;
      clickable = true;
    }
  } else if (!item.use_nominations) {
    // Recognition mode is the default for events; only an explicit true opts
    // an event into the nominations flow.
    hint = t("dates.send");
    HintIcon = Send;
    clickable = true;
  } else if (item.campaign_id && item.campaign_active) {
    hint = t("dates.nominate");
    HintIcon = Trophy;
    clickable = true;
  } else if (item.campaign_id && isAdmin) {
    hint = t("dates.finish_campaign_setup");
    HintIcon = Settings;
    clickable = true;
  } else if (item.campaign_id) {
    hint = t("dates.nominations_open_soon");
  } else if (isAdmin) {
    hint = t("dates.create_campaign");
    HintIcon = Plus;
    clickable = true;
  } else {
    hint = t("dates.no_campaign_yet");
  }

  const title = isPerson
    ? `${item.user?.full_name || item.user?.email || t("dates.someone")}`
    : item.event?.name || t("dates.event");

  const subtitle = (() => {
    if (item.kind === "anniversary") {
      return t("dates.anniversary_subtitle", { count: item.years, date: formatDate(item.date, fmt, t) });
    }
    if (item.kind === "birthday") return t("dates.birthday_subtitle", { date: formatDate(item.date, fmt, t) });
    return formatDate(item.date, fmt, t);
  })();

  const dimClass = hint
    ? "transition-all duration-200 ease-out group-hover:opacity-25 group-hover:scale-[0.98]"
    : "";

  return (
    <button
      type="button"
      onClick={() => clickable && onAction(item, { isAdmin })}
      disabled={!clickable}
      className={`group relative w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl text-left transition-colors duration-200 ${
        clickable ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"
      }`}
    >
      {isPerson && item.user ? (
        item.user.avatar_url ? (
          <img
            src={item.user.avatar_url}
            alt={title}
            className={`w-8 h-8 rounded-full object-cover flex-shrink-0 ${dimClass}`}
            style={{ border: `2px solid ${meta.color}` }}
          />
        ) : (
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ${dimClass}`}
            style={{
              ...getAvatarStyle(item.user.full_name || item.user.email || item.user.id),
              border: `2px solid ${meta.color}`,
            }}
          >
            {getUserInitials(title)}
          </div>
        )
      ) : (
        <div
          className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${dimClass}`}
          style={{ background: `${meta.color}1a` }}
        >
          <Icon style={{ color: meta.color, width: 18, height: 18 }} />
        </div>
      )}

      <div className={`min-w-0 flex-1 ${dimClass}`}>
        <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: meta.color }} />
          <span className="truncate">{title}</span>
        </div>
        <div className="text-xs text-gray-500 truncate">{subtitle}</div>
      </div>

      {hint && (
        <span
          className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg max-w-[85%] opacity-0 translate-x-3 transition-all duration-200 ease-out group-hover:opacity-100 group-hover:translate-x-0 ${
            clickable ? "text-white" : "bg-gray-800/95 text-white"
          }`}
          style={clickable ? { background: meta.color } : {}}
        >
          {HintIcon && <HintIcon className="w-3.5 h-3.5 flex-shrink-0" />}
          <span className="truncate">{hint}</span>
        </span>
      )}
    </button>
  );
};

export default function ImportantDatesRail({ compact = false }) {
  const { t } = useTranslation("feed");
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [enabled, setEnabled] = useState(true);

  const isAdmin = !!user && (user.is_superuser || user.role === "admin");

  useEffect(() => {
    let cancelled = false;
    importantDatesService
      .getImportantDates()
      .then((data) => {
        if (!cancelled) {
          setItems(data?.items || []);
          setEnabled(data?.enabled !== false);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAction = (item) => {
    if (item.kind === "birthday" || item.kind === "anniversary") {
      navigate(
        `/recognition?recipientId=${item.user.id}&typeId=${item.recognition_type_id}&occasion=${item.kind}`
      );
      return;
    }
    if (!item.use_nominations) {
      navigate(
        `/recognition?typeId=${item.recognition_type_id}&occasion=event&eventName=${encodeURIComponent(item.event.name)}`
      );
      return;
    }
    if (item.campaign_id && item.campaign_active) {
      navigate(`/nominations?campaignId=${item.campaign_id}`);
    } else if (item.campaign_id && isAdmin) {
      navigate(`/admin/campaigns/${item.campaign_id}`);
    } else if (isAdmin) {
      navigate(
        `/admin?tab=nominations&createCampaign=1&eventId=${item.event.id}&eventName=${encodeURIComponent(item.event.name)}`
      );
    }
  };

  if (error || !enabled) return null;

  return (
    // Width, stickiness and placement belong to .app-grid__rail now; this used
    // to carry `w-96 sticky top-8 mt-[62px]`, where the 62px was hand-tuned to
    // line up with the feed banner and drifted on any font or zoom change.
    <WidgetShell
      widgetKey="important_dates"
      icon={CalendarHeart}
      title={t("dates.title")}
      accentClass="text-purple-600"
      compact={compact}
      defaultCollapsed={compact}
      maxBody={bodyHeightForRows(compact ? 4 : 3)}
    >
      <div className="p-2">
        {loading ? (
          <div className="py-6 flex justify-center">
            <div className="animate-spin w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-6 px-3">
            {t("dates.empty")}
          </p>
        ) : (
          items.map((item, idx) => (
            <ImportantDateRow
              key={`${item.kind}-${item.user?.id || item.event?.id || idx}-${item.date}`}
              item={item}
              isAdmin={isAdmin}
              currentUserId={user?.id}
              onAction={handleAction}
            />
          ))
        )}
      </div>
    </WidgetShell>
  );
}
